/**
 * 2026-08-31 — client-side minimum lead time for scheduling a social post.
 *
 * Founder hit POST /:id/schedule's "scheduled_at must be in the future"
 * 422 on a real LinkedIn post: the approve request round-tripped 47s after
 * the picked time, so it was already stale by the time Schedule was
 * clicked. The server's own check is instant-of-request, always racy
 * against typing + network + read-the-form latency — this is a UI guard,
 * not a duplicate of the server rule.
 *
 * Dropped from 30 to 10 minutes (2026-09-19, founder feedback) — 30 was
 * more buffer than the actual round-trip risk warranted, and the picker
 * now self-corrects to now+30min whenever a too-soon value is chosen
 * (SocialPostEditor.tsx, shared by the calendar drawer and the piece
 * screen's Amplifications document since FEAT-092), so this threshold
 * mainly guards staleness while the form sits open, not the initial pick.
 *
 * Extracted from SocialCalendarView.tsx so the pure logic is testable
 * without a component-render harness (this repo's established pattern —
 * see ui-state.ts / run-errors.ts).
 */
export const MIN_SCHEDULE_LEAD_MINUTES = 10;

/**
 * True when `localValue` (a <input type="datetime-local"> value) is less
 * than MIN_SCHEDULE_LEAD_MINUTES from `now`. Empty/unparseable values are
 * never "too soon" — this only gates an actual chosen time.
 */
export function scheduledAtTooSoon(localValue: string, now: number = Date.now()): boolean {
  if (!localValue) return false;
  const t = new Date(localValue).getTime();
  if (Number.isNaN(t)) return false;
  return t < now + MIN_SCHEDULE_LEAD_MINUTES * 60_000;
}

// Convert ISO string -> value for <input type="datetime-local"> (local).
// FEAT-092 — extracted out of SocialCalendarView.tsx's PostDrawer into
// SocialPostEditor.tsx; FEAT-091 moved it again, here, since the piece
// screen's amplification-creation form needed it too and this file already
// exists precisely to be the one home for schedule-time logic.
export function isoToLocalInput(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * FEAT-091 — the default a new amplification's "scheduled at" field opens
 * with. Also the value SocialPostEditor's own self-correction snaps a
 * too-soon pick forward to (:413-424 there) — one constant, two callers,
 * so a future change to "how far ahead is the safe default" can't leave the
 * two disagreeing.
 */
export const DEFAULT_SCHEDULE_LEAD_MINUTES = 30;

export function defaultScheduledAtLocal(now: number = Date.now()): string {
  return isoToLocalInput(new Date(now + DEFAULT_SCHEDULE_LEAD_MINUTES * 60_000).toISOString());
}
